import { useEffect, useRef, useState, type ReactNode } from 'react';
import { avatarHue, initials, sizedPhotoUrl } from '../pages/directory/avatar-utils';

// NOTE: this is the single header-profile banner shared by the dashboard and My Info. Keep it
// identical on both surfaces. The avatar shows the profile photo (Google `picture`, cached at
// sign-in) when `photoUrl` is supplied, falling back to initials keyed off the name (same person =
// same colour everywhere) when there is no photo or it fails to load.

export type ProfileBannerMenuItem = {
  label: string;
  /** Invoked on click. Omit for a disabled/placeholder item. */
  onSelect?: () => void;
  /** Render the item greyed-out and non-interactive (e.g. "coming soon"). */
  disabled?: boolean;
  /** Hover tooltip. Defaults to "Coming soon" for disabled items; ignored when not set on enabled items. */
  title?: string;
};

type ProfileBannerProps = {
  /** Full name; shown as the heading and used to derive the initials avatar. */
  name: string;
  /** Profile photo URL (e.g. the Google `picture`). Shown in the avatar; falls back to initials. */
  photoUrl?: string | null;
  /** Optional line under the name (e.g. job title). */
  subtitle?: string;
  /** Optional action rendered as a pill at the right of the banner. */
  primaryAction?: ReactNode;
  /** Optional items for a kebab ("...") dropdown menu at the right. */
  menuItems?: ProfileBannerMenuItem[];
  /** Accessible label for the kebab trigger. Defaults to "More options". */
  menuLabel?: string;
  /** When true, render the heading as "Hi, {greetingName ?? name}!" (greeting form). */
  greeting?: boolean;
  /** Name shown in the greeting. Defaults to `name`. Use to pass first name only. */
  greetingName?: string;
  className?: string;
};

// Polished greeting banner for the top of a page: a green card with the user's
// initials avatar, name, and an optional subtitle. The right side can host an
// optional action pill and a kebab menu, so the component is reusable beyond Home.
export function ProfileBanner({
  name,
  photoUrl,
  subtitle,
  primaryAction,
  menuItems,
  menuLabel,
  greeting = false,
  greetingName,
  className = '',
}: ProfileBannerProps) {
  const hue = avatarHue(name);
  const hasActions = Boolean(primaryAction) || Boolean(menuItems?.length);
  const [photoFailed, setPhotoFailed] = useState(false);
  const showPhoto = Boolean(photoUrl) && !photoFailed;

  return (
    <header
      data-testid="profile-banner"
      className={`flex items-center justify-between gap-4 rounded-3xl bg-linear-150 from-hero-from to-hero-to px-[21px] py-[17px] text-white ${className}`}
    >
      <div className="flex min-w-0 items-center gap-5 sm:gap-6">
        <span
          aria-hidden
          style={showPhoto ? undefined : { backgroundColor: `hsl(${hue} 70% 90%)`, color: `hsl(${hue} 60% 30%)` }}
          // eslint-disable-next-line local/no-mode-locked-color -- the avatar sits ON the brand-coloured banner, not on a themed surface; a white halo is correct in both modes.
          className="inline-flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-full text-3xl font-semibold ring-4 ring-white/40 sm:h-[110px] sm:w-[110px] sm:text-4xl"
        >
          {showPhoto ? (
            <img
              src={sizedPhotoUrl(photoUrl as string, 320)}
              alt=""
              className="h-full w-full object-cover"
              referrerPolicy="no-referrer"
              onError={() => setPhotoFailed(true)}
            />
          ) : (
            initials(name)
          )}
        </span>
        <div className="min-w-0">
          <h1 className="truncate font-sans text-3xl font-extrabold leading-none text-white sm:text-4xl lg:text-5xl">
            {greeting ? `Hi, ${greetingName ?? name}!` : name}
          </h1>
          {subtitle ? (
            <p className="mt-1.5 truncate text-base font-medium text-white/80 sm:text-lg">{subtitle}</p>
          ) : null}
        </div>
      </div>

      {hasActions ? (
        <div className="flex shrink-0 items-center gap-3">
          {primaryAction}
          {menuItems?.length ? <KebabMenu items={menuItems} menuLabel={menuLabel} /> : null}
        </div>
      ) : null}
    </header>
  );
}

function KebabMenu({ items, menuLabel = 'More options' }: { items: ProfileBannerMenuItem[]; menuLabel?: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      {/* On-image treatment (translucent white over the banner photo), which no Button variant
          models; the box is still the sm control size. */}
      {/* eslint-disable-next-line local/no-self-sized-control -- on-image control; see above */}
      <button
        type="button"
        aria-label={menuLabel}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        // eslint-disable-next-line local/no-mode-locked-color -- kebab sits ON the brand-coloured banner; the white washes are ink-on-brand, correct in both modes.
        className="inline-flex h-8 w-8 items-center justify-center rounded-2xl bg-white/20 text-white transition-colors hover:bg-white/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
      >
        <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden>
          <circle cx="5" cy="12" r="1.6" />
          <circle cx="12" cy="12" r="1.6" />
          <circle cx="19" cy="12" r="1.6" />
        </svg>
      </button>
      {open ? (
        <div
          role="menu"
          // z-50 so the dropdown floats above sticky page chrome (e.g. My Info's sticky tab strip
          // and sidebar, both z-10) instead of being painted under the section below it.
          className="absolute right-0 top-full z-50 mt-2 w-44 rounded-xl border border-slate-200 bg-surface py-1"
        >
          {items.map((item) =>
            // Disabled items render as a non-interactive span (not a disabled button) so callers can
            // express "present but unavailable" states distinct from actionable buttons.
            item.disabled ? (
              <span
                key={item.label}
                role="menuitem"
                aria-disabled="true"
                title={item.title ?? 'Coming soon'}
                className="block w-full cursor-not-allowed px-4 py-2 text-left text-sm text-slate-400"
              >
                {item.label}
              </span>
            ) : (
              <button
                key={item.label}
                type="button"
                role="menuitem"
                title={item.title}
                onClick={() => {
                  setOpen(false);
                  item.onSelect?.();
                }}
                className="block w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 focus:outline-none focus-visible:bg-slate-50"
              >
                {item.label}
              </button>
            ),
          )}
        </div>
      ) : null}
    </div>
  );
}
